import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { MoreHorizontal, Plus } from 'lucide-react';
import type {
  BoardDashboard,
  DashboardAggregation,
  DashboardBucket,
  DashboardChartType,
  DashboardFacet,
  DashboardTile,
  DashboardTileCreateBody,
} from '@molt/shared';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { IconButton } from '@/components/IconButton';
import { LabelChip } from '@/components/LabelChip';
import { Menu } from '@/components/Menu';
import { labelSwatch } from '@/components/palette';
import {
  dashboardAggregationForTile,
  dashboardBucketPattern,
  dashboardBucketPercent,
  dashboardChartTypeLabel,
  dashboardFacetLabel,
} from '@/lib/board-dashboard';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface BoardDashboardViewProps {
  readonly dashboard: BoardDashboard;
  readonly readonly?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly onAddTile?: ((input: DashboardTileCreateBody) => void) | undefined;
  readonly onChangeTileFacet?:
    ((tileId: string, facet: DashboardFacet) => void) | undefined;
  readonly onChangeTileChartType?:
    ((tileId: string, chartType: DashboardChartType) => void) | undefined;
  readonly onRemoveTile?: ((tileId: string) => void) | undefined;
}

export function BoardDashboardView({
  dashboard,
  readonly = false,
  busy = false,
  onAddTile,
  onChangeTileFacet,
  onChangeTileChartType,
  onRemoveTile,
}: BoardDashboardViewProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [addOpen, setAddOpen] = useState(false);
  const [editingTileId, setEditingTileId] = useState<string | null>(null);
  const canConfigure = !readonly && dashboard.board.closed === false;
  const lockedReason = dashboard.board.closed
    ? t('boardDashboard.view.lockedClosed')
    : t('boardDashboard.view.lockedRole');
  const tileLockedReason = dashboard.board.closed
    ? t('boardDashboard.view.tileLockedClosed')
    : t('boardDashboard.view.tileLockedRole');

  function submitAdd(value: DashboardTileCreateBody): void {
    onAddTile?.(value);
    setAddOpen(false);
  }

  return (
    <section
      id="board-view-content"
      aria-label={t('boardDashboard.view.region')}
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-surface px-3 pb-3 text-ink"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-ink">
            {t('boardDashboard.view.heading')}
          </h2>
          <p className="text-card text-ink-muted">
            {t(
              'boardDashboard.view.tileCount',
              { count: dashboard.tiles.length, board: dashboard.board.title },
              dashboard.tiles.length,
            )}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant={addOpen ? 'toggle' : 'secondary'}
          // `aria-pressed`, not `pressed`: `Button` reads `pressed` only on
          // `variant='toggle'`, so the off state announced no state at all.
          aria-pressed={addOpen}
          iconStart={<Plus aria-hidden="true" className="size-4" />}
          disabled={!canConfigure || busy}
          disabledReason={!canConfigure ? lockedReason : undefined}
          onClick={() => setAddOpen((open) => !open)}
        >
          {t('boardDashboard.view.addTile')}
        </Button>
      </div>

      {addOpen ? (
        <DashboardTileForm
          title={t('boardDashboard.view.addFormTitle')}
          submitLabel={t('boardDashboard.view.addFormSubmit')}
          busy={busy}
          onCancel={() => setAddOpen(false)}
          onSubmit={submitAdd}
        />
      ) : null}

      {dashboard.tiles.length === 0 ? (
        <EmptyState
          tone="neutral"
          title={t('boardDashboard.view.emptyTitle')}
          body={t('boardDashboard.view.emptyBody')}
          action={
            <Button
              type="button"
              size="sm"
              variant="primary"
              disabled={!canConfigure || busy}
              disabledReason={!canConfigure ? lockedReason : undefined}
              onClick={() => setAddOpen(true)}
            >
              {t('boardDashboard.view.addTile')}
            </Button>
          }
        />
      ) : (
        <ol
          className="grid gap-3 xl:grid-cols-2"
          aria-label={t('boardDashboard.view.tileList')}
        >
          {dashboard.tiles.map((tile) => {
            const aggregation = dashboardAggregationForTile(dashboard, tile);
            return (
              <li key={tile.id}>
                <DashboardTilePanel
                  tile={tile}
                  aggregation={aggregation}
                  readonly={!canConfigure}
                  lockedReason={tileLockedReason}
                  busy={busy}
                  editing={editingTileId === tile.id}
                  onEdit={() => setEditingTileId(tile.id)}
                  onCancelEdit={() => setEditingTileId(null)}
                  onSubmitEdit={(value) => {
                    if (value.facet !== tile.facet) {
                      onChangeTileFacet?.(tile.id, value.facet);
                    }
                    if (value.chartType !== tile.chartType) {
                      onChangeTileChartType?.(tile.id, value.chartType);
                    }
                    setEditingTileId(null);
                  }}
                  onRemove={() => onRemoveTile?.(tile.id)}
                />
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

interface DashboardTileFormProps {
  readonly title: string;
  readonly submitLabel: string;
  readonly initialValue?: DashboardTileCreateBody | undefined;
  readonly busy?: boolean | undefined;
  readonly onCancel: () => void;
  readonly onSubmit: (value: DashboardTileCreateBody) => void;
}

const FACETS: readonly DashboardFacet[] = ['list', 'member', 'label', 'due_date'];
const CHART_TYPES: readonly DashboardChartType[] = ['bar', 'pie'];
const TOKEN_STROKES = [
  'var(--color-accent)',
  'var(--color-ok)',
  'var(--color-warn)',
  'var(--color-bad)',
  'var(--color-ink-muted)',
] as const;

function DashboardTileForm({
  title,
  submitLabel,
  initialValue = { facet: 'list', chartType: 'bar' },
  busy = false,
  onCancel,
  onSubmit,
}: DashboardTileFormProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [facet, setFacet] = useState<DashboardFacet>(initialValue.facet);
  const [chartType, setChartType] = useState<DashboardChartType>(initialValue.chartType);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    onSubmit({ facet, chartType });
  }

  return (
    <form
      aria-label={title}
      className="flex flex-wrap items-end gap-2 rounded-card border border-border-strong bg-panel p-3 shadow-card"
      onSubmit={submit}
    >
      <label className="flex min-w-48 flex-col gap-1 text-card font-medium text-ink">
        {t('boardDashboard.view.facetLabel')}
        <select
          className="h-9 rounded-card border border-border-strong bg-surface px-2 text-card text-ink"
          value={facet}
          disabled={busy}
          onChange={(event) => setFacet(event.currentTarget.value as DashboardFacet)}
        >
          {FACETS.map((option) => (
            <option key={option} value={option}>
              {dashboardFacetLabel(t, option)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex min-w-40 flex-col gap-1 text-card font-medium text-ink">
        {t('boardDashboard.view.chartTypeLabel')}
        <select
          className="h-9 rounded-card border border-border-strong bg-surface px-2 text-card text-ink"
          value={chartType}
          disabled={busy}
          onChange={(event) =>
            setChartType(event.currentTarget.value as DashboardChartType)
          }
        >
          {CHART_TYPES.map((option) => (
            <option key={option} value={option}>
              {dashboardChartTypeLabel(t, option)}
            </option>
          ))}
        </select>
      </label>
      <Button type="submit" size="sm" variant="primary" busy={busy}>
        {submitLabel}
      </Button>
      <Button type="button" size="sm" variant="quiet" disabled={busy} onClick={onCancel}>
        {t('boardDashboard.view.cancel')}
      </Button>
    </form>
  );
}

interface DashboardTilePanelProps {
  readonly tile: DashboardTile;
  readonly aggregation: DashboardAggregation | null;
  readonly readonly: boolean;
  readonly lockedReason: string;
  readonly busy: boolean;
  readonly editing: boolean;
  readonly onEdit: () => void;
  readonly onCancelEdit: () => void;
  readonly onSubmitEdit: (value: DashboardTileCreateBody) => void;
  readonly onRemove: () => void;
}

function DashboardTilePanel({
  tile,
  aggregation,
  readonly,
  lockedReason,
  busy,
  editing,
  onEdit,
  onCancelEdit,
  onSubmitEdit,
  onRemove,
}: DashboardTilePanelProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const title = dashboardFacetLabel(t, tile.facet);

  return (
    <article className="min-h-80 rounded-card border border-border-strong bg-panel p-3 shadow-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-card font-semibold text-ink">{title}</h3>
          <p className="text-xs text-ink-muted">
            {t('boardDashboard.view.chartCaption', {
              type: dashboardChartTypeLabel(t, tile.chartType),
            })}
          </p>
        </div>
        {readonly ? (
          <p className="max-w-44 text-right text-xs text-ink-muted">{lockedReason}</p>
        ) : (
          <Menu
            align="end"
            trigger={
              <IconButton
                label={t('boardDashboard.view.tileActions', { tile: title })}
                size="sm"
              >
                <MoreHorizontal aria-hidden="true" className="size-4" />
              </IconButton>
            }
            items={[
              {
                id: 'edit',
                label: t('boardDashboard.view.editTile'),
                disabled: busy,
                onSelect: onEdit,
              },
              {
                id: 'remove',
                label: t('boardDashboard.view.removeTile'),
                destructive: true,
                disabled: busy,
                onSelect: onRemove,
              },
            ]}
          />
        )}
      </div>

      {editing ? (
        <div className="mt-3">
          <DashboardTileForm
            title={t('boardDashboard.view.editFormTitle', { tile: title })}
            submitLabel={t('boardDashboard.view.editFormSubmit')}
            initialValue={tile}
            busy={busy}
            onCancel={onCancelEdit}
            onSubmit={onSubmitEdit}
          />
        </div>
      ) : null}

      {aggregation === null ? (
        <p role="status" className="mt-4 text-card text-ink-muted">
          {t('boardDashboard.view.chartUnavailable')}
        </p>
      ) : (
        <DashboardChart tile={tile} aggregation={aggregation} />
      )}
    </article>
  );
}

interface DashboardChartProps {
  readonly tile: DashboardTile;
  readonly aggregation: DashboardAggregation;
}

function DashboardChart({ tile, aggregation }: DashboardChartProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [activeBucketId, setActiveBucketId] = useState<string | null>(null);
  const buckets = aggregation.buckets;
  const activeBucket = buckets.find((bucket) => bucket.id === activeBucketId) ?? null;
  const tooltipText =
    activeBucket === null
      ? null
      : t(
          'boardDashboard.view.tooltip',
          { title: activeBucket.title, count: activeBucket.count },
          activeBucket.count,
        );

  if (tile.facet === 'label' && buckets.length === 0) {
    return (
      <div className="mt-4 rounded-card border border-dashed border-border-strong bg-surface px-3 py-6 text-card text-ink-muted">
        {t('boardDashboard.view.noLabels')}
      </div>
    );
  }

  return (
    <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(14rem,18rem)]">
      {tile.chartType === 'pie' ? (
        <PieChart
          aggregation={aggregation}
          activeBucketId={activeBucketId}
          onActivate={setActiveBucketId}
        />
      ) : (
        <BarChart
          aggregation={aggregation}
          activeBucketId={activeBucketId}
          onActivate={setActiveBucketId}
        />
      )}

      <div className="min-w-0">
        {aggregation.bucketCountsMayExceedTotalCards ? (
          <p className="mb-2 rounded-card bg-surface-raised px-2 py-1 text-xs text-ink-muted">
            {tile.facet === 'member'
              ? t(
                  'boardDashboard.view.exceedMember',
                  { count: aggregation.totalCards },
                  aggregation.totalCards,
                )
              : t(
                  'boardDashboard.view.exceedLabel',
                  { count: aggregation.totalCards },
                  aggregation.totalCards,
                )}
          </p>
        ) : null}
        {tooltipText === null ? null : (
          <p role="tooltip" className="mb-2 text-card font-medium text-ink">
            {tooltipText}
          </p>
        )}
        <ul
          aria-label={t('boardDashboard.view.bucketCounts', {
            facet: dashboardFacetLabel(t, tile.facet),
          })}
          className="grid gap-1.5"
        >
          {buckets.map((bucket, index) => (
            <li key={bucket.id}>
              <BucketButton
                bucket={bucket}
                index={index}
                active={bucket.id === activeBucketId}
                onActivate={setActiveBucketId}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

interface ChartProps {
  readonly aggregation: DashboardAggregation;
  readonly activeBucketId: string | null;
  readonly onActivate: (bucketId: string | null) => void;
}

function BarChart({ aggregation, activeBucketId, onActivate }: ChartProps): ReactElement {
  const { t } = useInterfaceLanguage();
  return (
    <div
      role="group"
      aria-label={t('boardDashboard.view.barChart', {
        facet: dashboardFacetLabel(t, aggregation.facet),
      })}
      className="grid content-start gap-2 p-1"
    >
      {aggregation.buckets.map((bucket, index) => {
        const percent = dashboardBucketPercent(bucket, aggregation.buckets);
        const patternProps = bucketPatternProps(bucket);
        return (
          <button
            key={bucket.id}
            type="button"
            aria-label={t(
              'boardDashboard.view.barLabel',
              { title: bucket.title, count: bucket.count },
              bucket.count,
            )}
            className={cn('grid gap-1 border border-transparent p-1 text-left', {
              'border-accent bg-surface-raised': activeBucketId === bucket.id,
            })}
            onMouseEnter={() => onActivate(bucket.id)}
            onMouseLeave={() => onActivate(null)}
            onFocus={() => onActivate(bucket.id)}
            onBlur={() => onActivate(null)}
          >
            <span className="flex items-center justify-between gap-2 text-xs text-ink-muted">
              <span className="truncate">{bucket.title}</span>
              <span className="font-medium text-ink">{bucket.count}</span>
            </span>
            <span className="h-3 rounded-full bg-border" aria-hidden="true">
              <span
                className={cn(
                  'block h-full rounded-full',
                  patternProps === undefined &&
                    aggregation.facet === 'label' &&
                    'outline outline-1 outline-offset-1 outline-border-strong',
                  patternProps !== undefined &&
                    'label-chip-pattern relative isolate overflow-hidden',
                )}
                style={{
                  width: `${percent}%`,
                  background: bucketStroke(bucket, index),
                }}
                {...patternProps}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PieChart({ aggregation, activeBucketId, onActivate }: ChartProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const total = aggregation.buckets.reduce((sum, bucket) => sum + bucket.count, 0);
  const segments = useMemo(() => {
    let offset = 0;
    return aggregation.buckets.map((bucket, index) => {
      const share = total === 0 ? 0 : (bucket.count / total) * 100;
      const segment = { bucket, index, offset, share };
      offset += share;
      return segment;
    });
  }, [aggregation.buckets, total]);

  return (
    <div
      role="group"
      aria-label={t('boardDashboard.view.pieChart', {
        facet: dashboardFacetLabel(t, aggregation.facet),
      })}
      className="flex min-h-56 items-center justify-center p-2"
    >
      {total === 0 ? (
        <p className="text-card text-ink-muted">{t('boardDashboard.view.emptyChart')}</p>
      ) : (
        <div className="relative size-52">
          <svg viewBox="0 0 120 120" className="size-52" aria-hidden="true">
            {segments.map(({ bucket, index, offset, share }) => (
              <circle
                key={bucket.id}
                cx="60"
                cy="60"
                r="42"
                fill="none"
                stroke={bucketStroke(bucket, index)}
                strokeWidth={activeBucketId === bucket.id ? 22 : 18}
                strokeDasharray={`${share} ${100 - share}`}
                strokeDashoffset={-offset}
                pathLength="100"
                transform="rotate(-90 60 60)"
                opacity={
                  activeBucketId === null || activeBucketId === bucket.id ? 1 : 0.42
                }
              />
            ))}
          </svg>
          {segments.map(({ bucket, offset, share }) => {
            const angle = ((offset + share / 2) / 100) * Math.PI * 2 - Math.PI / 2;
            const radius = 4.4;
            const x = 50 + Math.cos(angle) * radius * 10;
            const y = 50 + Math.sin(angle) * radius * 10;
            return (
              <button
                key={bucket.id}
                type="button"
                aria-label={t(
                  'boardDashboard.view.segmentLabel',
                  { title: bucket.title, count: bucket.count },
                  bucket.count,
                )}
                className="absolute size-8 -translate-x-1/2 -translate-y-1/2 rounded-full border border-transparent focus-visible:border-focus focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                style={{ left: `${x}%`, top: `${y}%` }}
                onMouseEnter={() => onActivate(bucket.id)}
                onMouseLeave={() => onActivate(null)}
                onFocus={() => onActivate(bucket.id)}
                onBlur={() => onActivate(null)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

interface BucketButtonProps {
  readonly bucket: DashboardBucket;
  readonly index: number;
  readonly active: boolean;
  readonly onActivate: (bucketId: string | null) => void;
}

function BucketButton({
  bucket,
  index,
  active,
  onActivate,
}: BucketButtonProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const patternProps = bucketPatternProps(bucket);

  return (
    <button
      type="button"
      aria-label={t(
        'boardDashboard.view.bucketLabel',
        { title: bucket.title, count: bucket.count },
        bucket.count,
      )}
      className={cn(
        'flex w-full items-center gap-2 rounded-card border border-border bg-surface-raised px-2 py-1.5 text-left text-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
        active && 'border-accent',
      )}
      onMouseEnter={() => onActivate(bucket.id)}
      onMouseLeave={() => onActivate(null)}
      onFocus={() => onActivate(bucket.id)}
      onBlur={() => onActivate(null)}
    >
      <span
        aria-hidden="true"
        className="label-chip-pattern relative isolate size-3 shrink-0 overflow-hidden rounded-full"
        style={{ background: bucketStroke(bucket, index) }}
        {...patternProps}
      />
      <span className="min-w-0 flex-1 truncate">{bucketLabel(bucket)}</span>
      <span className="shrink-0 font-semibold text-ink">{bucket.count}</span>
    </button>
  );
}

function bucketPatternProps(
  bucket: DashboardBucket,
): { readonly 'data-label-pattern': string } | undefined {
  const pattern = dashboardBucketPattern(bucket);
  return pattern === undefined ? undefined : { 'data-label-pattern': pattern };
}

function bucketLabel(bucket: DashboardBucket): ReactElement | string {
  if ('label' in bucket) {
    return <LabelChip colour={bucket.label.colour} name={bucket.label.name} size="sm" />;
  }
  return bucket.title;
}

function bucketStroke(bucket: DashboardBucket, index: number): string {
  if ('label' in bucket) {
    return labelSwatch(bucket.label.colour)?.fill ?? 'var(--color-surface-raised)';
  }
  return TOKEN_STROKES[index % TOKEN_STROKES.length]!;
}
